import React, { useState, useEffect } from 'react';
import { FaTimes, FaSave, FaDollarSign, FaEye, FaEyeSlash } from 'react-icons/fa';
import { updatePhoto } from '../services/api';

const PhotoEditModal = ({ photo, onClose, onSaved, blobUrl }) => {
  const [price, setPrice] = useState('');
  const [isPurchasable, setIsPurchasable] = useState(false);
  const [visibility, setVisibility] = useState('public');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (photo) {
      setPrice(photo.price != null ? photo.price : '');
      setIsPurchasable(!!photo.is_purchasable);
      setVisibility(photo.visibility || 'public');
      setError('');
    }
  }, [photo]);

  if (!photo) return null;

  const handleSave = async () => {
    const parsedPrice = price === '' ? null : parseFloat(price);
    if (parsedPrice !== null && (isNaN(parsedPrice) || parsedPrice < 0)) {
      setError('Price must be a positive number or empty');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const response = await updatePhoto(photo.id, {
        price: parsedPrice,
        is_purchasable: isPurchasable,
        visibility,
      });
      onSaved(response.photo);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const previewSrc = blobUrl || photo.previewUrl;

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white', borderRadius: '14px',
          padding: '2rem', width: '100%', maxWidth: '420px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          position: 'relative',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ margin: 0, color: 'var(--primary-orange)' }}>Edit Photo</h3>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: '1.2rem', color: 'var(--gray)',
            }}
          >
            <FaTimes />
          </button>
        </div>

        {/* Preview */}
        {previewSrc && (
          <img
            src={previewSrc}
            alt="Preview"
            style={{
              width: '100%', height: '180px', objectFit: 'cover',
              borderRadius: '8px', marginBottom: '1.5rem',
              background: 'var(--light-gray)',
            }}
            onError={e => { e.target.style.display = 'none'; }}
          />
        )}

        {error && (
          <div className="error-message" style={{ marginBottom: '1rem' }}>{error}</div>
        )}

        {/* Visibility */}
        <div className="form-group">
          <label style={{ fontWeight: '600', color: 'var(--dark-gray)' }}>Visibility</label>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              type="button"
              onClick={() => setVisibility('public')}
              style={{
                flex: 1, padding: '0.65rem',
                background: visibility === 'public' ? 'var(--primary-green)' : 'var(--off-white)',
                color: visibility === 'public' ? 'white' : 'var(--dark-gray)',
                border: '2px solid',
                borderColor: visibility === 'public' ? 'var(--primary-green)' : 'var(--light-gray)',
                borderRadius: '8px', cursor: 'pointer', fontWeight: '600',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem',
              }}
            >
              <FaEye /> Public
            </button>
            <button
              type="button"
              onClick={() => setVisibility('private')}
              style={{
                flex: 1, padding: '0.65rem',
                background: visibility === 'private' ? '#555' : 'var(--off-white)',
                color: visibility === 'private' ? 'white' : 'var(--dark-gray)',
                border: '2px solid',
                borderColor: visibility === 'private' ? '#555' : 'var(--light-gray)',
                borderRadius: '8px', cursor: 'pointer', fontWeight: '600',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem',
              }}
            >
              <FaEyeSlash /> Private
            </button>
          </div>
        </div>

        {/* Price */}
        <div className="form-group">
          <label style={{ fontWeight: '600', color: 'var(--dark-gray)' }}>
            Price (leave empty for free)
          </label>
          <div style={{ position: 'relative' }}>
            <FaDollarSign style={{
              position: 'absolute', left: '12px', top: '50%',
              transform: 'translateY(-50%)', color: 'var(--gray)',
            }} />
            <input
              type="number"
              min="0"
              step="0.01"
              placeholder="0.00"
              value={price}
              onChange={e => setPrice(e.target.value)}
              style={{ paddingLeft: '2rem' }}
            />
          </div>
        </div>

        {/* Is Purchasable */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.75rem',
          padding: '1rem', background: 'var(--off-white)',
          borderRadius: '8px', marginBottom: '1.5rem',
        }}>
          <input
            id="isPurchasable"
            type="checkbox"
            checked={isPurchasable}
            onChange={e => setIsPurchasable(e.target.checked)}
            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
          />
          <label htmlFor="isPurchasable" style={{ cursor: 'pointer', margin: 0 }}>
            <strong>Available for purchase</strong>
            <div style={{ fontSize: '0.85rem', color: 'var(--gray)', marginTop: '2px' }}>
              Allow users to buy this photo
            </div>
          </label>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button
            onClick={onClose}
            style={{
              flex: 1, padding: '0.8rem',
              background: 'var(--light-gray)', color: 'var(--dark-gray)',
              border: 'none', borderRadius: '8px',
              cursor: 'pointer', fontWeight: '600',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-submit"
            style={{ flex: 1, padding: '0.8rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}
          >
            <FaSave /> {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PhotoEditModal;
